import { useCallback, useEffect, useRef, useState } from 'react'
import { readSSE } from '../../../utils/sse'
import { toWireAttachment, toDisplayAttachment } from './attachments'

/**
 * State shape:
 *   messages: Message[]
 *     - { id, role: 'user', text }
 *     - { id, role: 'assistant', _sdkMsgId, segments, usage?, cost?, isError? }
 *
 *   A segment is one of:
 *     - { type: 'text', text }
 *     - { type: 'thinking', text }          (kept collapsed in the UI)
 *     - { type: 'tool_use', id, name, input, result?: { text, isError } }
 *
 * The hook owns the network call + the SDK event → segment mapping. The UI
 * consumes `messages` and renders bubbles + ToolUseCard inline.
 *
 * A turn OUTLIVES its connection. The sidecar keeps the run in a registry and
 * numbers every SSE frame (`id: <seq>`), so when the stream drops — wifi blink,
 * proxy timeout, laptop asleep — the hook reconnects on `/attach` with the last
 * seq it saw and the rest is replayed. Before that, a dropped stream simply froze
 * the bubble mid-sentence, and the worst case was silent: a stream cut cleanly
 * ends the reader without throwing, so there wasn't even an error to show.
 * `sawTerminal` is what tells the two apart — a real turn always ends on `done`.
 */

const ENDPOINT = '/api/builder/chat/stream'
const ATTACH_ENDPOINT = '/api/builder/chat/attach'
const STOP_ENDPOINT = '/api/builder/chat/stop'

// Reconnection backoff. The first attempt is IMMEDIATE — a reload or a brief
// blip should rejoin at once, and a genuinely dead network costs one cheap
// failed fetch before the spacing starts. Then ~45s of retries: long enough to
// ride out a wifi handover, short enough that a dead run doesn't leave the
// editor watching a spinner.
const RECONNECT_DELAYS = [0, 1000, 2000, 4000, 8000, 15000, 15000]

// Silence after which a stream is presumed dead. The sidecar heartbeats every
// 15s, so this is three missed beats. It exists for the failure that throws
// nothing at all: a half-open socket (laptop asleep, wifi handover, NAT entry
// expiring) where `reader.read()` simply never resolves.
const STREAM_IDLE_MS = 45000

const MUTATING_TOOLS = new Set([
  'mcp__orbit-builder__create_block_def',
  'mcp__orbit-builder__update_block_def',
])
// Dashboard-layout writes invalidate the CANVAS (page.blocs), not the
// block registry — tracked separately so we fire the right refresh (L4).
const DASHBOARD_MUTATORS = new Set([
  'mcp__orbit-builder__set_dashboard_layout',
])

/**
 * Rebuild an assistant turn's segments from the final SDK `assistant` message
 * content, position-by-position. Position i of `items` maps to block index i,
 * matching how live `stream_event` deltas are indexed — so this reconciliation
 * is idempotent whether or not the turn was pre-built by streaming. Tool
 * results already attached (they arrive in later `user` events) are preserved
 * by matching on tool id.
 *
 * This is also what makes a REPLAY safe: a reconnection may have missed deltas
 * (the run's buffer sheds those first when it overflows), and the `assistant`
 * frame puts the text back authoritatively.
 */
function rebuildSegmentsFromItems(prevSegments, items) {
  return items.map((item) => {
    if (item.type === 'text') return { type: 'text', text: item.text ?? '' }
    if (item.type === 'thinking') return { type: 'thinking', text: item.thinking ?? '' }
    if (item.type === 'tool_use') {
      const existing = prevSegments.find(s => s?.type === 'tool_use' && s.id === item.id)
      return { type: 'tool_use', id: item.id, name: item.name, input: item.input, result: existing?.result ?? null }
    }
    return { type: 'text', text: '' }
  })
}

/**
 * Apply `fn` to the segments of the assistant turn identified by `msgId`,
 * returning a new messages array (or the same reference when nothing changed).
 * Used by the live `stream_event` path to mutate the in-flight turn.
 */
function patchAssistantTurn(messages, msgId, fn) {
  if (!msgId) return messages
  const idx = messages.findIndex(m => m.role === 'assistant' && m._sdkMsgId === msgId)
  if (idx === -1) return messages
  const turn = messages[idx]
  const segments = fn(turn.segments)
  if (segments === turn.segments) return messages
  const copy = messages.slice()
  copy[idx] = { ...turn, segments }
  return copy
}

function attachToolResult(messages, toolUseId, result) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i]
    if (msg.role !== 'assistant') continue
    // `s?.` et pas `s.` : `find` — contrairement à `map` et `filter` — parcourt les
    // TROUS d'un tableau creux, et un tour dont les premiers blocs n'ont jamais été
    // livrés en a. Le remplissage dans `content_block_start` est ce qui les évite ;
    // ceci est la ceinture qui va avec ces bretelles.
    const seg = (msg.segments ?? []).find(s => s?.type === 'tool_use' && s.id === toolUseId)
    if (seg) {
      seg.result = result
      return
    }
  }
}

/**
 * Block type touched by a create/update mutator, read from its tool_result.
 * Both tools answer a JSON blob carrying `{nid, type, …}` — the type is what
 * the canvas needs to reload (and, when absent from the page, to add). Returns
 * null on anything unparseable: the registry still gets refreshed, only the
 * per-block follow-up is skipped.
 */
function extractBlockType(text) {
  try {
    const parsed = JSON.parse(text)
    return typeof parsed?.type === 'string' ? parsed.type : null
  }
  catch { return null }
}

// `crypto.randomUUID`, not a timestamp plus four base36 characters of
// `Math.random()`. The id travels in the attach query string — so into nginx logs,
// proxies and browser history — and it used to be the ONLY thing standing between
// an editor and someone else's turn. The server now keys runs by owner, which is
// the real guarantee; this is the defence in depth that should have been there
// anyway. (`randomUUID` needs a secure context: on plain http it is absent, which
// is why the Playwright harness polyfills it.)
const newConversationId = () => (
  globalThis.crypto?.randomUUID
    ? `b-${crypto.randomUUID()}`
    : `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`
)

/**
 * Plain-text transcript of what is on screen, sent alongside the question.
 *
 * The agent's own session is the primary memory; this is the backstop for when
 * it can't be resumed (transcripts wiped by a redeploy, a conversation reopened
 * on another instance). Text only — thinking and tool calls are the session's
 * job, not something worth re-sending.
 */
function toHistory(messages) {
  return messages
    .filter(m => !m.isError)
    .map(m => ({
      role: m.role,
      text: m.role === 'user'
        ? (m.text ?? '')
        : (m.segments ?? []).filter(s => s?.type === 'text').map(s => s.text).join('\n'),
    }))
    .filter(m => m.text.trim() !== '')
}

/**
 * Cost-dashboard dimension for this turn. Mirrors the same `match` in
 * BuilderChatProxyController::stream() — the reconnection is a GET with no body,
 * so the label has to travel on the query string for the resumed turn to land in
 * the same bucket as the one it continues.
 */
function costContext(blockContext, host) {
  if (blockContext) return 'builder_block'
  if (host) return 'builder_dashboard'
  return 'builder_catalogue'
}

/**
 * L'adresse de l'écran courant, telle qu'elle sera relue : le chemin et sa
 * requête. Ni l'hôte (une instance n'en a qu'un, il n'apprend rien) ni le
 * fragment (il ne désigne pas une page).
 */
const currentOrigin = () => (
  typeof window === 'undefined' ? '' : `${window.location.pathname}${window.location.search}`
)

/** Abortable sleep — a pending backoff must not outlive a Stop. */
function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms)
    signal?.addEventListener('abort', () => {
      clearTimeout(timer)
      reject(new DOMException('Aborted', 'AbortError'))
    }, { once: true })
  })
}

export function useBuilderChatStream({ onRegistryInvalidate, onDashboardInvalidate, host } = {}) {
  // Kept in a ref so the memoized `send` always reads the current host/callback
  // without re-creating the callback (or risking a stale closure).
  const hostRef = useRef(host)
  hostRef.current = host
  const onDashboardInvalidateRef = useRef(onDashboardInvalidate)
  onDashboardInvalidateRef.current = onDashboardInvalidate
  const [messages, setMessages] = useState([])
  // Mirrored so `send` can read the conversation as it stands without being
  // re-created on every streamed token.
  const messagesRef = useRef(messages)
  messagesRef.current = messages
  const [isStreaming, setIsStreaming] = useState(false)
  // True only while the hook is trying to get BACK onto a turn it lost. The
  // turn is still running server-side throughout — this is a transport state,
  // not a pause.
  const [isReconnecting, setIsReconnecting] = useState(false)
  // Position in the sidecar's queue while the turn waits for a slot, or null.
  // A wait is not a failure and must not read like one — the editor is told
  // where they stand instead of watching an idle panel.
  const [queuePosition, setQueuePosition] = useState(null)
  const [error, setError] = useState(null)
  const [conversationId, setConversationId] = useState(newConversationId)
  const conversationIdRef = useRef(conversationId)
  conversationIdRef.current = conversationId
  // D'où la conversation est PARTIE. Latché au premier message et plus jamais
  // rechangé : ce qui situe une conversation dans une liste, c'est l'écran où la
  // question a été posée — pas celui où l'éditeur se trouvait en la poursuivant.
  const [origin, setOriginState] = useState(null)
  const originRef = useRef(null)
  const isStreamingRef = useRef(false)
  // Id of the agent-side session holding this conversation (prompts, tool
  // calls, answers). Sent back on every turn so the agent resumes instead of
  // starting from scratch — without it there is no iterating on a previous
  // answer. Mirrored in a ref so `send` reads the current value without being
  // re-created mid-conversation.
  const sessionIdRef = useRef(null)
  const [sessionId, setSessionIdState] = useState(null)
  const abortRef = useRef(null)
  // Id of the assistant message currently being streamed. `stream_event` deltas
  // (content_block_start/delta) don't carry the message id — only the preceding
  // `message_start` does — so we latch it here to route deltas to the right turn.
  const streamingMsgIdRef = useRef(null)
  // Everything scoped to the turn in flight. In a ref, not closure locals,
  // because a turn now spans SEVERAL connections: the initial POST and any
  // number of reconnections have to accumulate into the same place.
  const turnRef = useRef(null)

  useEffect(() => () => abortRef.current?.abort(), [])

  const setSessionId = useCallback((id) => {
    sessionIdRef.current = id ?? null
    setSessionIdState(id ?? null)
  }, [])

  const setOrigin = useCallback((path) => {
    originRef.current = path || null
    setOriginState(path || null)
  }, [])

  /**
   * Tell the sidecar to abandon the run. Since a run outlives its request, a
   * closing socket cancels nothing — without this call the agent finishes (and
   * bills) a turn the editor explicitly stopped, or walked away from.
   */
  const stopRun = useCallback((id) => {
    if (!id) return
    fetch(STOP_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationId: id }),
      // Best effort: the UI stops either way, and the run's own duration guard
      // bounds the damage if this never lands.
      keepalive: true,
    }).catch(() => {})
  }, [])

  /**
   * Lâcher le FLUX sans abandonner le TOUR.
   *
   * `abort()` fait tout le travail : il ferme la socket — donc rend le worker
   * PHP-FPM du pool `chat`, dont il n'y a que douze pour tout le monde — et il
   * arrête la boucle de reconnexion, parce que le sommeil du backoff est
   * abortable et que `driveTurn` ne rappelle `reconnect` que si le signal est
   * intact. Il n'y a donc rien à ajouter pour empêcher un rebranchement sur la
   * conversation qu'on vient de quitter.
   *
   * Ce qu'il ne fait PAS : annuler le run. C'est tout l'intérêt — le tour
   * continue côté sidecar, l'historique le montre « en cours », et on le rejoint
   * d'un clic. Seul le ■ (`stop`) abandonne pour de bon.
   *
   * `turnRef` et `isStreamingRef` sont cédés ICI, de façon synchrone : le
   * `driveTurn` du tour qu'on lâche ne se dénouera qu'au tick suivant, et un
   * appelant qui enchaîne sur `resumeRun` serait sinon refusé par sa garde.
   */
  const detach = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    turnRef.current = null
    isStreamingRef.current = false
    setIsStreaming(false)
    setIsReconnecting(false)
    setQueuePosition(null)
  }, [])

  const reset = useCallback(() => {
    // Plus de `stopRun` ici : ouvrir une conversation neuve LÂCHE le flux, ça
    // n'abandonne pas le tour. Avant, « Nouvelle conversation » — et le ✨ d'un
    // bloc, qui passe par là — tuait le tour en cours en silence, en laissant
    // une ligne de coût à 0 $ pour un travail à moitié payé.
    detach()
    setMessages([])
    setError(null)
    setSessionId(null)
    setOrigin(null)
    setConversationId(newConversationId())
  }, [detach, setOrigin, setSessionId])

  /**
   * Re-open a stored conversation: restoring the messages is not enough, the
   * conversation's identity has to come back with them or the agent answers
   * the next question with no idea what is displayed above it.
   */
  const resumeConversation = useCallback(({ conversationId: id, sessionId: session, messages: stored, origin: startedFrom } = {}) => {
    // Le tour de la conversation qu'on quitte CONTINUE : on se contente de
    // lâcher son flux. C'est ce qui permet d'en mener plusieurs de front.
    detach()
    setMessages(stored ?? [])
    setError(null)
    setSessionId(session ?? null)
    // Repris tel qu'il a été enregistré : une conversation rouverte garde
    // l'écran d'où elle est partie, pas celui où on la rouvre.
    setOrigin(startedFrom ?? null)
    setConversationId(id ?? newConversationId())
  }, [detach, setOrigin, setSessionId])

  /**
   * Le ■ : le SEUL abandon volontaire, et le seul appelant qui reste de
   * `stopRun`. Depuis qu'un run survit à sa requête, une socket qui se ferme
   * ne l'annule plus — et depuis que changer de conversation ne fait que
   * détacher, ce bouton est le seul frein de l'éditeur.
   */
  const stop = useCallback(() => {
    stopRun(conversationIdRef.current)
    detach()
  }, [detach, stopRun])

  /**
   * One SDK event → state. Shared verbatim by the initial stream and every
   * reconnection, so a replayed frame is handled exactly like a live one.
   *
   * The mapping is idempotent by construction (segments addressed by block
   * index, turns by `_sdkMsgId`, tool results by tool id), which is what lets a
   * replay overlap what was already rendered without duplicating anything.
   */
  const applyEvent = useCallback((turn, { id, event, data }) => {
    // Ce tour n'est plus celui qu'on suit : ses trames appartiennent à une
    // conversation qu'on a quittée. Un tour détaché ne s'arrête PAS au même tick
    // — son `consume` rejette et son `driveTurn` se dénoue après —, donc sans
    // cette garde les trames retardataires de A s'écriraient dans le transcript
    // de B. Et le pire est qu'on ne s'en apercevrait qu'à la relecture : le
    // rendu reste parfaitement crédible.
    if (!turn || turnRef.current !== turn) return

    if (id !== null && id !== undefined && id !== '') {
      // Opaque on purpose: the server now numbers frames `<runId>:<seq>`, so the
      // cursor identifies WHICH turn it belongs to. Parsing it here would only
      // create a second place that has to agree with the server about the format.
      turn.lastId = String(id)
      // Anything that is not the queue notice itself means the turn is running.
      if (event !== 'queued') setQueuePosition(null)
      // A NUMBERED frame is proof the stream is live again — drop the
      // « reconnexion… » banner here rather than when the attach call returns,
      // because that call runs until the TURN ends: the editor would watch
      // « reconnexion en cours » for the whole answer they are already reading.
      // Only numbered frames count: the relay injects UNnumbered `error` frames
      // when it is the transport that failed, which is the opposite of proof.
      setIsReconnecting(false)
    }

    // Every SDK message carries its session id; `done` echoes it as
    // `sessionId`. Latch it as soon as it shows up so an interrupted turn
    // still leaves the conversation resumable.
    const streamSessionId = data?.session_id ?? data?.sessionId
    if (streamSessionId && streamSessionId !== sessionIdRef.current) {
      setSessionId(streamSessionId)
    }

    if (event === 'stream_event') {
      // Live partial deltas from the SDK (includePartialMessages). We render
      // text/thinking token-by-token; tool_use input is left empty until the
      // final `assistant` message lands (the input streams as raw JSON
      // fragments not worth rendering mid-flight). parent_tool_use_id is set
      // only for sub-agent streams, which this agent never spawns — skip them.
      if (data.parent_tool_use_id) return
      const ev = data.event
      if (!ev) return
      if (ev.type === 'message_start') {
        const msgId = ev.message?.id
        if (!msgId) return
        streamingMsgIdRef.current = msgId
        setMessages(prev => {
          const last = prev.at(-1)
          if (last?.role === 'assistant' && last._sdkMsgId === msgId) return prev
          return [...prev, { id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, role: 'assistant', _sdkMsgId: msgId, segments: [] }]
        })
      }
      else if (ev.type === 'content_block_start') {
        const idx = ev.index
        const cb = ev.content_block ?? {}
        if (!Number.isInteger(idx) || idx < 0) return
        setMessages(prev => patchAssistantTurn(prev, streamingMsgIdRef.current, segs => {
          const next = segs.slice()
          // Adresser les segments par index de bloc est ce qui rend un replay idempotent,
          // mais un run dont le tampon a débordé perd ses frames les PLUS ANCIENNES : le
          // bloc 2 peut arriver sur un tableau vide. Écrire en 2 y laisserait deux trous,
          // et un trou n'est pas `undefined` — il traverse `map`/`filter` intact et
          // n'explose que plus tard, chez le consommateur qui utilise `find`. On comble.
          while (next.length < idx) next.push({ type: 'text', text: '' })
          if (cb.type === 'text') next[idx] = { type: 'text', text: cb.text ?? '' }
          else if (cb.type === 'thinking') next[idx] = { type: 'thinking', text: cb.thinking ?? '' }
          else if (cb.type === 'tool_use') next[idx] = { type: 'tool_use', id: cb.id, name: cb.name, input: {}, result: null }
          else return segs
          return next
        }))
      }
      else if (ev.type === 'content_block_delta') {
        const idx = ev.index
        const d = ev.delta ?? {}
        setMessages(prev => patchAssistantTurn(prev, streamingMsgIdRef.current, segs => {
          const seg = segs[idx]
          if (!seg) return segs
          if (d.type === 'text_delta' && seg.type === 'text') {
            const next = segs.slice(); next[idx] = { ...seg, text: seg.text + (d.text ?? '') }; return next
          }
          if (d.type === 'thinking_delta' && seg.type === 'thinking') {
            const next = segs.slice(); next[idx] = { ...seg, text: seg.text + (d.thinking ?? '') }; return next
          }
          return segs
        }))
      }
      // content_block_stop / message_delta / message_stop carry no UI state.
    }
    else if (event === 'assistant') {
      const sdkMsg = data.message
      const sdkMsgId = sdkMsg?.id
      const items = sdkMsg?.content ?? []
      setMessages(prev => {
        const last = prev.at(-1)
        const usable = last?.role === 'assistant' && last._sdkMsgId === sdkMsgId
        const baseSegments = usable ? last.segments : []
        // Final, authoritative content for this message — rebuild segments by
        // block index, reconciling whatever the live stream_event path built.
        const segments = rebuildSegmentsFromItems(baseSegments, items)
        const built = usable
          ? { ...last, segments }
          : { id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, role: 'assistant', _sdkMsgId: sdkMsgId, segments }
        return usable ? [...prev.slice(0, -1), built] : [...prev, built]
      })
      // Track which tool_use ids belong to a mutator so we know whether
      // to invalidate the registry once their result comes back.
      for (const item of items) {
        if (item.type === 'tool_use' && MUTATING_TOOLS.has(item.name)) {
          turn.mutators.add(item.id)
        }
        if (item.type === 'tool_use' && DASHBOARD_MUTATORS.has(item.name)) {
          turn.dashboardMutators.add(item.id)
        }
      }
    }
    else if (event === 'user') {
      // SDK emits a synthetic "user" message holding the tool_result.
      const content = data.message?.content ?? []
      for (const item of content) {
        if (item.type !== 'tool_result') continue
        const text = Array.isArray(item.content)
          ? item.content.map(c => c.text ?? '').join('')
          : (typeof item.content === 'string' ? item.content : JSON.stringify(item.content))
        setMessages(prev => {
          const copy = prev.map(m => m.role === 'assistant' ? { ...m, segments: m.segments.map(s => ({ ...s })) } : m)
          attachToolResult(copy, item.tool_use_id, { text, isError: !!item.is_error })
          return copy
        })
        if (!item.is_error && turn.mutators.has(item.tool_use_id)) {
          turn.registryTouched = true
          const blockType = extractBlockType(text)
          if (blockType) turn.touchedTypes.add(blockType)
          // Re-fire-safe: clear so we don't record twice if more results come.
          turn.mutators.delete(item.tool_use_id)
        }
        if (!item.is_error && turn.dashboardMutators.has(item.tool_use_id)) {
          onDashboardInvalidateRef.current?.()
          turn.dashboardMutators.delete(item.tool_use_id)
        }
      }
    }
    else if (event === 'result') {
      setMessages(prev => {
        const last = prev.at(-1)
        if (last?.role !== 'assistant') return prev
        return [
          ...prev.slice(0, -1),
          {
            ...last,
            usage: data.usage,
            cost: data.total_cost_usd,
            isError: !!data.is_error,
            errorMessage: data.is_error ? data.result : undefined,
          },
        ]
      })
    }
    else if (event === 'queued') {
      // The sidecar caps how many turns run at once; this one is waiting. Re-sent
      // each time the queue advances, so the number stays true.
      setQueuePosition(data?.position ?? null)
    }
    else if (event === 'done') {
      turn.sawTerminal = true
    }
    else if (event === 'gone') {
      // The run is not in the sidecar's registry: it finished long ago and its
      // buffer expired, or the container restarted. Nothing to come back to, so
      // stop the backoff here — and SAY so, or the editor is left staring at a
      // bubble that stopped mid-sentence with no explanation.
      turn.gone = true
      turn.sawTerminal = true
      setError("La réponse n'est plus disponible — la connexion a été interrompue trop longtemps.")
    }
    else if (event === 'error') {
      // Two different errors wear the same name, told apart by whether the frame
      // is NUMBERED. A numbered `error` came from the run itself and ends the
      // turn. An unnumbered one was injected by the Drupal relay (curl died, the
      // stream stalled) — the run is very probably still alive upstream, so it
      // must NOT be terminal or we would give up on a turn we could rejoin.
      const numbered = id !== null && id !== undefined && id !== ''
      turn.lastError = data?.message ?? 'Erreur inconnue'
      if (numbered) {
        turn.sawTerminal = true
        setError(turn.lastError)
      }
    }
  }, [setSessionId])

  /** Drain one SSE response into `applyEvent`. */
  /**
   * Drain one SSE response into `applyEvent`.
   *
   * `idleMs` is what turns a stream that went quiet into an ordinary
   * end-of-stream: the sidecar heartbeats every 15s, so three missed beats mean
   * the socket is dead even though nothing ever threw. Without it, a laptop
   * waking up or a wifi handover leaves the reader waiting forever — the run
   * finishes server-side and the editor watches an empty bubble.
   */
  const consume = useCallback(async (res, turn) => {
    for await (const frame of readSSE(res, { withEvent: true, idleMs: STREAM_IDLE_MS })) {
      applyEvent(turn, frame)
      // On sort aussi quand ce tour a perdu la main : sans ça la boucle draine
      // jusqu'au bout un flux dont plus personne ne veut les trames, en tenant
      // le worker PHP-FPM qui va avec.
      if (turn.sawTerminal || turnRef.current !== turn) return
    }
  }, [applyEvent])

  /**
   * Get back onto a turn we lost, from the last seq actually received.
   *
   * Runs until the turn ends, the run turns out to be gone, the editor stops, or
   * the backoff is exhausted. Everything it receives goes through the very same
   * `applyEvent`, so a resumed turn renders identically to one that never broke.
   *
   * The first delay is 0: a reload or a brief blip should rejoin immediately,
   * and a genuinely dead network costs one cheap failed fetch before the backoff
   * starts spacing attempts out.
   */
  const reconnect = useCallback(async (signal, turn) => {
    if (!turn) return
    setIsReconnecting(true)
    try {
      for (const delay of RECONNECT_DELAYS) {
        if (signal.aborted || turn.sawTerminal || turnRef.current !== turn) return
        try {
          // The sleep is INSIDE the try: it rejects on abort, and letting that
          // escape would leave an unhandled rejection every time the editor
          // presses Stop during a reconnection.
          if (delay > 0) await sleep(delay, signal)

          const url = `${ATTACH_ENDPOINT}?conversationId=${encodeURIComponent(turn.conversationId)}`
            + `&from=${encodeURIComponent(turn.lastId ?? '')}&context=${encodeURIComponent(turn.context)}`
          const res = await fetch(url, { credentials: 'include', signal })
          if (!res.ok) continue
          await consume(res, turn)
          if (turn.sawTerminal) return
          // The rejoined stream died too — say so again before the next attempt.
          setIsReconnecting(true)
        }
        catch (err) {
          if (err.name === 'AbortError') return
          // Still offline, or the relay died again — keep trying.
        }
      }
    }
    finally {
      // Même garde que partout ailleurs : un tour détaché qui se dénoue ne doit
      // pas effacer la bannière de reconnexion du tour qui a pris sa place.
      if (turnRef.current === turn) setIsReconnecting(false)
    }

    // Backoff exhausted. A `gone` run already spoke for itself above; what is
    // left is a transport that never came back — say what failed last, if the
    // relay told us anything more precise than "it broke".
    if (!turn.sawTerminal && turnRef.current === turn) {
      setError(turn.lastError ?? 'La connexion a été interrompue et n’a pas pu être rétablie.')
    }
  }, [consume])

  /**
   * Follow one turn to its end, whatever connection it takes to get there.
   *
   * Shared by `send` (which opens the stream with a POST) and `resumeRun` (which
   * has no stream to open and goes straight to the attach loop). Keeping ONE
   * driver is what guarantees a turn rejoined after a reload behaves exactly
   * like one that was never interrupted — same accumulators, same terminal
   * detection, same end-of-turn refresh.
   *
   * @param openStream
   *   `(signal) => Promise<Response>` for a turn we are starting, or null for a
   *   turn already running somewhere that we only want to rejoin.
   */
  const driveTurn = useCallback(async ({ conversationId: cid, context, blockContext = null, openStream = null, fromId = null }) => {
    setError(null)
    setIsStreaming(true)
    isStreamingRef.current = true

    const controller = new AbortController()
    abortRef.current = controller
    streamingMsgIdRef.current = null

    // What the agent changed during THIS turn, and where the stream got to.
    // Accumulated across the initial connection AND any reconnection, consumed
    // once the turn ends (see the `finally` below).
    const turn = {
      conversationId: cid,
      context,
      lastId: fromId,
      sawTerminal: false,
      gone: false,
      lastError: null,
      touchedTypes: new Set(),
      registryTouched: false,
      mutators: new Set(),
      dashboardMutators: new Set(),
    }
    turnRef.current = turn

    try {
      if (openStream) {
        try {
          const res = await openStream(controller.signal)
          // A refused request (403, 400…) is final — there is no run to rejoin.
          if (!res.ok) throw Object.assign(new Error(`HTTP ${res.status}`), { final: true })
          await consume(res, turn)
        }
        catch (err) {
          if (err.name === 'AbortError') turn.sawTerminal = true
          else if (err.final) {
            turn.sawTerminal = true
            setError(err.message)
          }
          // Anything else is a transport failure mid-stream. The run is still
          // alive in the sidecar — fall through to the reconnection below rather
          // than declaring the answer lost.
        }
      }

      // The stream ended without the turn ending. Either it was cut, or it was
      // truncated cleanly, or it simply went quiet (none of which throws — the
      // silent cases this whole mechanism exists for). A resumed turn arrives
      // here directly, with no stream of its own to drain first.
      if (!turn.sawTerminal && !controller.signal.aborted) {
        await reconnect(controller.signal, turn)
      }
    }
    finally {
      // Ce tour n'est peut-être plus celui qu'on suit : détaché, il s'est dénoué
      // pendant qu'un AUTRE prenait la main. Écrire les drapeaux globaux ici
      // éteindrait le tour en cours — plus de ■, composeur réactivé, `turnRef`
      // vidé sous les trames qui arrivent.
      if (turnRef.current === turn) {
        setIsStreaming(false)
        isStreamingRef.current = false
        setQueuePosition(null)
        turnRef.current = null
      }
      // End of turn — ONE refresh for the whole turn, carrying the block types
      // the agent created/updated so the canvas can reload them (and drop a
      // brand-new one on the page). Fired here rather than per tool_result so a
      // multi-tool turn refetches once, and so a turn stopped mid-flight still
      // surfaces what already landed server-side. It sits AFTER the reconnection
      // above on purpose: firing it on every dropped connection would remount
      // blocks in the middle of a turn.
      //
      // A turn with a PINNED context always refreshes, without waiting for the
      // mutator detection to agree: the editor acted on something they can see,
      // so the answer must land on screen — not only when a `tool_result`
      // happened to parse. The refresh is cheap: the canvas reloads THE NAMED
      // types only (one def, not the whole catalogue), and keeps the object
      // identity of a def whose signature is unchanged — so a turn that merely
      // talked about the block remounts nothing and flickers nowhere.
      //
      // It also matters for the CREATION intents: the canvas consumes the pending
      // placement on this event, so firing it unconditionally is what keeps a
      // placer from staying armed and dropping a block, turns later, somewhere
      // the editor never asked for.
      // A missing intent means `edit`, exactly as the sidecar reads it: a cached
      // older bundle must not silently stop reloading the block it pinned.
      const isEdit = blockContext && (blockContext.intent ?? 'edit') === 'edit'
      const pinnedType = isEdit ? blockContext.type : null
      if (pinnedType) turn.touchedTypes.add(pinnedType)
      // DÉTACHÉ N'EST PAS FINI. Lâcher un flux — ce que fait tout nouveau « + »,
      // et la fermeture du panneau — passe par un `abort`, et le tour, lui,
      // CONTINUE dans le sidecar. Annoncer une fin ici faisait consommer au
      // canevas la place réservée d'un tour encore au travail : son carré
      // s'éteignait, et le bloc qu'il finissait par produire n'avait plus où
      // atterrir. C'est le sondage qui annonce la fin de ces tours-là
      // (cf. BuilderChatPanel, `newlyFinished`), quand elle a VRAIMENT eu lieu.
      //
      // Ce qui rendait cette annonce indispensable a disparu : le placement
      // n'est plus un emplacement unique qu'un autre tour pourrait dépenser, il
      // appartient à sa conversation (cf. blockPlacement).
      if (!controller.signal.aborted && (turn.registryTouched || blockContext)) {
        // La conversation VOYAGE avec l'invalidation : c'est elle qui dit quelle
        // place réservée ce tour-ci vient remplir. Sans elle, deux « + » de front
        // rendaient le canevas incapable de distinguer les deux demandes — il
        // dépensait la première venue, et le bloc atterrissait au mauvais endroit.
        onRegistryInvalidate?.({ types: [...turn.touchedTypes], conversationId: turn.conversationId })
      }
    }
  }, [consume, onRegistryInvalidate, reconnect])

  /**
   * Rejoin a turn that was in flight when the page went away.
   *
   * A reload is the most ordinary way an editor loses a stream — far more common
   * than a network cut — and it used to lose the answer outright: nothing
   * reconnected, no request was made, and the run finished alone in the sidecar.
   * The panel calls this on reopening when the stored conversation says a turn
   * had not ended.
   */
  const resumeRun = useCallback(({ conversationId: cid, lastId = null, context = 'builder_catalogue' } = {}) => {
    if (!cid || isStreamingRef.current) return
    return driveTurn({ conversationId: cid, context, fromId: lastId })
  }, [driveTurn])

  /** Where the turn in flight got to, for the panel to persist. Null when idle. */
  const getPendingTurn = useCallback(() => {
    const turn = turnRef.current
    return turn ? { lastId: turn.lastId, context: turn.context } : null
  }, [])

  // `blockContext` — what the editor pinned: a block to edit (the ✨), a creation
  // from scratch, or a creation modelled on an existing block. Re-sent on EVERY
  // turn while the chip stays pinned, so the agent keeps the context even when a
  // session resume falls back to a fresh run.
  const send = useCallback(async (text, attachments = [], blockContext = null, options = {}) => {
    const trimmed = (text ?? '').trim()
    if ((!trimmed && attachments.length === 0) || isStreaming) return

    // Images/PDFs are forwarded to the model for this turn only; we persist a
    // lightweight display copy (no full-size base64) on the user message.
    const wireAttachments = attachments.map(toWireAttachment)
    const displayAttachments = attachments.map(toDisplayAttachment)

    const history = toHistory(messagesRef.current)

    // Latché au PREMIER message, puis renvoyé tel quel à chaque tour : le
    // sidecar réécrit son enregistrement à la fin de chaque tour, donc sans ce
    // report une deuxième question posée depuis une autre page réécrirait
    // l'adresse de départ.
    const startedFrom = originRef.current || currentOrigin()
    if (originRef.current !== startedFrom) setOrigin(startedFrom)

    setMessages(prev => [...prev, {
      id: `u-${Date.now()}`,
      role: 'user',
      text: trimmed,
      ...(displayAttachments.length ? { attachments: displayAttachments } : {}),
    }])

    return driveTurn({
      conversationId,
      context: costContext(blockContext, hostRef.current),
      blockContext,
      openStream: (signal) => fetch(ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // The proxy rejects an empty message, so fall back to a default when
          // the user sends files with no text.
          message: trimmed || 'Analyse le(s) fichier(s) joint(s).',
          conversationId,
          ...(sessionIdRef.current ? { sessionId: sessionIdRef.current } : {}),
          ...(history.length ? { history } : {}),
          ...(wireAttachments.length ? { attachments: wireAttachments } : {}),
          // Tell the agent which dashboard is open (bundle + id), so it can
          // read/write it via get/set_dashboard_layout.
          ...(hostRef.current
            ? { dashboardContext: { id: hostRef.current.id, bundle: hostRef.current.kind } }
            : {}),
          // Bloc ciblé : le sidecar en fait un bloc « Current context » ET borne
          // `update_block_def` à ce `defNid` (garde-fou serveur, pas un paramètre
          // que le modèle choisit).
          ...(blockContext ? { blockContext } : {}),
          // Purement descriptif : sert la liste des tours, jamais l'agent.
          ...(startedFrom ? { origin: startedFrom } : {}),
          // Le consentement de l'éditeur à ce que l'agent écrive des fichiers.
          // Il n'autorise RIEN par lui-même : le proxy Drupal le retire de ce
          // corps et repose la question à la session avant de poser l'en-tête
          // que le sidecar, lui, écoute. Ce que le navigateur envoie ici est une
          // intention, jamais un droit.
          ...(options.allowFileWrite ? { allowFileWrite: true } : {}),
        }),
        signal,
      }),
    })
  }, [conversationId, driveTurn, isStreaming, setOrigin])

  return { messages, isStreaming, isReconnecting, queuePosition, error, send, stop, detach, reset, resumeConversation, resumeRun, getPendingTurn, conversationId, sessionId, origin, setMessages }
}
